<template>
	<SuiDataTableRow
		:aria-label="$t('spa__general.loading')"
		class="task-list-row-skeleton"
		:class="classAddons"
	>
		<template v-for="cell in orderedCells" :key="cell.columnId">
			<SuiDataTableCell
				v-if="cell.columnId === TABLE_COLUMN.Collapsible"
				class="task-list-row-skeleton__expand-cell"
			>
				<TaskListRowExpandButton
					v-if="id && hasSubtasks"
					:is-open="isOpen"
					:is-disabled="isSubtasksDisabled"
					:expand-collapse-tooltip="expandCollapseTooltip"
					@click="$emit('expand-collapse', id)"
				/>
			</SuiDataTableCell>

			<SuiDataTableCell
				v-else-if="cell.columnId === TABLE_COLUMN.Assignee"
				class="task-list-row-skeleton__assignee-cell"
			>
				<slot />
				<UserAssignSelectContainerSkeleton
					:animate
					:is-owner-enabled="useTaskOwnerSetting"
				/>
			</SuiDataTableCell>

			<SuiDataTableCell
				v-else-if="cell.columnId === TABLE_COLUMN.Actions"
			/>

			<SuiDataTableCell
				v-else-if="cell.columnId === TABLE_COLUMN.Title"
				class="task-list-row-skeleton__title-cell"
			>
				<div class="task-list-row-skeleton__title">
					<TaskPriority :priority="Priority.Normal" is-done />
					<span v-if="name">{{ name }}</span>
					<SuiSkeleton
						v-else
						:animate
						:height="isSubtask ? '1.25rem' : '1.5rem'"
					/>
				</div>
			</SuiDataTableCell>

			<SuiDataTableCell
				v-else-if="cell.columnId === TABLE_COLUMN.Subitems"
			/>

			<SuiDataTableCell
				v-else-if="cell.columnId === TABLE_COLUMN.Comments"
				:align="cell.align"
			>
				<CommentCountSkeleton :animate />
			</SuiDataTableCell>

			<SuiDataTableCell v-else-if="cell.columnId === TABLE_COLUMN.Status">
				<SuiSkeleton :animate height="2.5rem" />
			</SuiDataTableCell>

			<SuiDataTableCell
				v-else-if="cell.columnId === TABLE_COLUMN.ActivityType"
			/>

			<SuiDataTableCell
				v-else-if="cell.columnId === TABLE_COLUMN.Progress"
			>
				<SuiProgressBar size="compact" />
			</SuiDataTableCell>

			<SuiDataTableCell
				v-else-if="
					[
						TABLE_COLUMN.StartDate,
						TABLE_COLUMN.DueDate,
						TABLE_COLUMN.Planned,
						TABLE_COLUMN.Done,
						TABLE_COLUMN.Remaining,
						TABLE_COLUMN.Budget,
						TABLE_COLUMN.RemainingBudget,
					].includes(cell.columnId)
				"
				:align="cell.align"
				:is-number="cell.isNumber"
			/>

			<SuiDataTableCell
				v-else-if="cell.columnId === TABLE_COLUMN.MarkDone"
				:align="cell.align"
			>
				<TimeEntryDoneSkeleton />
			</SuiDataTableCell>
		</template>
	</SuiDataTableRow>
</template>

<script setup lang="ts">
import { Priority, type Task } from '@/generated/graphql';
import {
	SuiDataTableCell,
	SuiDataTableRow,
	SuiSkeleton,
	SuiProgressBar,
	type SuiDataTableColumnType,
} from '@developers/scoro-ui';
import TaskPriority from '../../TaskPriority/TaskPriority.vue';
import { CommentCountSkeleton } from '@/features/Comment';
import { UserAssignSelectContainerSkeleton } from '@/features/User';
import { TimeEntryDoneSkeleton } from '@/features/TimeEntry';
import { computed } from 'vue';
import { Setting } from '@/constants/settings';
import { useBooleanSetting } from '@/composables/settings/useSettings';
import { useTaskListRowCells } from './composables';
import { TABLE_COLUMN } from '../constants';
import TaskListRowExpandButton from './TaskListRowExpandButton.vue';

export interface TaskListRowSkeletonProps {
	id?: Task['id'];
	name?: Task['name'];
	animate?: boolean;
	isSubtask?: boolean;
	isOpen?: boolean;
	isSubtasksDisabled?: boolean;
	expandCollapseTooltip?: string;
	colDefinition?: SuiDataTableColumnType[];
	subtasksCount?: number;
}

const {
	id = undefined,
	name = '',
	animate = true,
	isSubtask = false,
	isOpen = false,
	isSubtasksDisabled = false,
	expandCollapseTooltip = '',
	colDefinition = [],
	subtasksCount = undefined,
} = defineProps<TaskListRowSkeletonProps>();

defineEmits<{
	(e: 'expand-collapse', id: Task['id']): void;
}>();

const [useTaskOwnerSetting] = useBooleanSetting(Setting.USE_TASK_OWNER);

const colDefinitionRef = computed(() => colDefinition);

const { orderedCells } = useTaskListRowCells({
	colDefinition: colDefinitionRef,
});

const hasSubtasks = computed(() => {
	return !isSubtask && subtasksCount && subtasksCount > 0;
});

const classAddons = computed(() => ({
	'task-list-row-skeleton--subtask': isSubtask,
	'task-list-row-skeleton--has-subtasks': hasSubtasks.value,
}));
</script>

<style lang="scss">
.task-list-row-skeleton {
	&__expand-cell {
		--sui-data-table-cell-padding: 0;
		--sui-data-table-side-spacing: 0;
	}

	&__assignee-cell {
		--sui-data-table-cell-padding: 1.5rem 1rem;
	}

	&__title-cell {
		padding-left: 1rem;
	}

	&__title {
		font-size: 1.625rem;
		font-weight: $scoro-fw-bold;
		display: flex;
		align-items: center;
		gap: 0.5rem;
	}

	&--subtask {
		--sui-data-table-cell-border-bottom: 0;

		.task-list-row-skeleton__title {
			gap: 0.5rem;
			font-weight: $scoro-fw-normal;
		}

		td:nth-child(2),
		td:nth-child(4) {
			padding-left: 3rem;
		}

		td:first-child {
			position: relative;

			&::before {
				content: ' ';
				position: absolute;
				width: 1px;
				top: 0;
				left: calc(50% - 2px);
				bottom: 0;
				background-color: $neutral-30;
			}

			&::after {
				content: ' ';
				position: absolute;
				height: 1px;
				top: 50%;
				right: 0;
				left: calc(50% - 2px);
				background-color: $neutral-30;
			}
		}
	}

	&--expanded {
		--sui-data-table-cell-border-bottom: 0;

		z-index: 2;
		box-shadow: 0 8px 8px -4px rgb(73 79 89 / 9%);
	}

	&--has-subtasks {
		.task-list-row-expand-button {
			visibility: visible;
		}
	}

	&:hover {
		.task-list-row-expand-button {
			visibility: visible;
		}

		.task-list-row-task__add-subtask {
			visibility: visible;
		}
	}

	&:has(+ .task-list-row-add-task--subtask) {
		td:first-child {
			&::before {
				bottom: 50%;
			}
		}
	}
}
</style>
